import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
  Show,
  TableComponent,
  Tabs,
} from "components";
import { FC, useEffect, useMemo } from "react";
import { useKEKOffers, useOffers } from "hooks/data/use-offers";
import { useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions } from "utils";

import { Action } from "components/buttons/action-button";
import AttachSupportingDocumentsContainer from "./attach-supporting-documents";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import CommentContainer from "./comment";
import CreateOfferContainer from "./create";
import { DocumentPlusIcon } from "@heroicons/react/24/outline";
import FilterMenu from "./filter";
import { LocationGenerics } from "router/location";
import { Offers_rows } from "_graphql/queries/__generated__/Offers";
import { PublishedStatus } from "_graphql/__generated__/globalTypes";
import RemoveContainer from "./remove";
import SendMessageContainer from "pages/place-it-offers/message-thread";
import UpdateOfferContainer from "./update";
import ViewOfferSlipContainer from "./view-slip";
import _ from "lodash";
import clsx from "clsx";
import { getDomain } from "router/filter";
import moment from "moment";
import numeral from "numeral";
import { status_type } from "_graphql/__generated__/globalTypes-kek";
import { usePagination } from "hooks";
import { useUpdateBubble } from "hooks/data/use-update-bubble";

type Props = {};

const tabs = [
  { name: "Open Offers", href: "OPEN" },
  { name: "Pending Offers", href: "PENDING" },
];

const CreateSlipKEKPage: FC<Props> = (props) => {
  const [modal, setModal] = useUrlState("modal");
  const [orderStatus] = useUrlState("orderStatus");
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { offset, limit, setPage } = usePagination();
  const { updateBubble } = useUpdateBubble();
  const domain = getDomain();

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      search: (searchParams.search || "").toString(),
      args: {
        status: orderStatus ? [orderStatus] : ["PENDING", "OPEN"],
        insurer_id: searchParams.insurer,
        payment_status: [],
        approval_status: status_type.UNAPPROVED,
        claim_status: [],
        offer_from: searchParams.offer_from || "",
        class_of_business_id: searchParams?.business,
        expiry_status: searchParams?.expiry_status || undefined,
        staff_id: searchParams?.employee ? searchParams?.employee : undefined,
        offset,
        limit,
        search: searchParams.search || "",
      },
    }),
    [
      searchParams.insurer,
      searchParams.business,
      searchParams.employee,
      limit,
      offset,
      orderStatus,
      searchParams.page,
      searchParams.pageSize,
      searchParams.search,
      searchParams.expiry_status,
      searchParams.offer_from,
    ]
  );

  const { offers, loading, refetch, count } = useKEKOffers(filter as any);

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const expandItem = (id: string) => {
    navigate({
      to: `./${id}/view`,
    });
  };

  const updateThreadBubble = (ids: string[]) => {
    updateBubble({
      variables: {
        ids,
      },
    })
      .then(() => {})
      .catch((err) => {});
  };

  useEffect(() => {
    if (orderStatus) {
      setPage(1);
    }
  }, [orderStatus]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() =>
          withPermissions(["*:*", "offers:*", "offers:create"])(
            <OfficeButton onClick={() => setModal("create")}>
              <DocumentPlusIcon className="w-5 h-5 mr-2" />
              Create New Slip
            </OfficeButton>
          )
        }
      />
      <Tabs tabs={tabs} orderStatus={orderStatus} defaultTab="All Offers" />
      {/* {JSON.stringify({ count, limit, offset })} */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"offers"}
            loading={loading}
            hasSearch
            emptyMessage="No offers found"
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count || 0}
                currentSize={offers.length}
                setOffset={setPage}
              />
            )}
            columns={[
              {
                name: "Policy #",
                accessor: "0.policy_number",
                render: (value) => (
                  <div className="flex flex-col items-start flex-wrap break-all break-words">
                    <div className="flex flex-row items-center gap-x-1">
                      {value?.offer_published &&
                        value?.offer_published?.published_status ===
                          PublishedStatus.PUBLISHED && (
                          <CheckBadgeIcon
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content="This Offer is Published"
                            className={clsx("w-4 h-4 mr-1", "text-green-600")}
                          />
                        )}
                      <span className="font-bold text-xs overflow-hidden text-ellipsis  ">
                        {value.offer_detail?.policy_number || "N/A"}{" "}
                        <Show if={Boolean(value.offer_retrocedent?.created_at)}>
                          <span>
                            -{" "}
                            <span className="text-xs text-red-500 italic">
                              Retro
                            </span>
                          </span>
                        </Show>
                      </span>
                    </div>
                    <div
                      className={clsx(
                        "p-0.5 rounded-full",
                        {
                          " text-green-600": value.offer_from === "INTERNAL",
                          " text-yellow-600": value.offer_from !== "INTERNAL",
                        },
                        "text-xs font-medium"
                      )}
                    >
                      <span>{value.offer_from}</span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Reinsured",
                accessor: "insurer.insurer_company_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.insurer?.insurer_company_name || "N/A"}
                    </span>
                    <span className="font-light">
                      {value.insurer?.insurer_company_email || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Insured",
                accessor: "offer_detail.insured_by",
                render: (value) => {
                  const details = JSON.parse(
                    value?.offer_detail?.offer_details || "[]"
                  );
                  return (
                    <div className="flex flex-col text-xs ">
                      <span className="font-medium">
                        {value.offer_detail?.insured_by || "N/A"}
                      </span>
                      <span className="font-light">
                        {[
                          "Motor Comprehensive Fleet",
                          "Motor Comprehensive",
                          "Motor Comprehensive (Automobile Fac Facility)",
                        ].includes(value?.classofbusiness?.business_name || "")
                          ? details?.find(
                              (el: { [x: string]: string }) =>
                                el?.["keydetail"] === "Vehicle Reg No" ||
                                el?.["keydetail"] === "Vehicle Reg No."
                            )?.value
                          : ""}
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "Class Of Business",
                accessor: "classofbusiness.business_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.classofbusiness?.business_name || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Sum Insured",
                accessor: "sum_insured",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail?.currency}{" "}
                      {numeral(value.sum_insured).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Rate",
                accessor: "rate",
              },
              {
                name: "Business State",
                accessor: "business_state",
              },
              {
                name: "Payment Type",
                accessor: "payment_type",
                render(data, index) {
                  return (
                    <span className="text-xs">
                      {_.startCase(
                        Object.keys(
                          JSON.parse(data.offer_detail?.payment_type || "{}")
                        ).at(0) || "N/A"
                      )}
                    </span>
                  );
                },
              },
              {
                name: "Total Securities",
                accessor: "total_securities",
                render(data) {
                  return (
                    <span className="text-xs">
                      {data?.offer_participant?.length || 0}
                    </span>
                  );
                },
              },
              {
                name: "Status",
                accessor: "offer_status",
                render: ({ offer_status }) => (
                  <span
                    className={clsx("text-xs", {
                      "text-blue-600": offer_status === "OPEN",
                      "text-yellow-600": offer_status === "PENDING",
                    })}
                  >
                    {offer_status}
                  </span>
                ),
              },
              {
                name: "Offer Date",
                accessor: "created_at",
                render: (value) => (
                  <span>
                    {moment(value.created_at).format("ddd MMM D, YYYY")}
                  </span>
                ),
              },
              {
                name: "Created By",
                accessor: "employee.employee_first_name",
                render: (value) => (
                  <div className="flex flex-row leading-0">
                    <span>
                      {value?.employee?.employee_first_name}{" "}
                      {value?.employee?.employee_last_name}
                    </span>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    <ActionButton
                      action="view"
                      tooltip="View Documents & History"
                      onClick={dispatchAction(
                        value.offer_id +
                          ":" +
                          value?.offer_from +
                          ":" +
                          value?.place_it_id,
                        "resolve"
                      )}
                    />
                    <ActionButton
                      action="comment"
                      tooltip="View Comments"
                      onClick={dispatchAction(value?.offer_id || "", "comment")}
                    />
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:update",
                    ])(
                      <ActionButton
                        action="update"
                        tooltip="Edit Offer"
                        onClick={dispatchAction(value.offer_id, "update")}
                      />
                    )}
                    <ActionButton
                      action="resolve"
                      tooltip="View Offer"
                      onClick={() => expandItem(value?.offer_id)}
                    />
                    <Show if={value.offer_from === "INTERNAL"}>
                      {withPermissions([
                        "*:*",
                        "offers:*",
                        "offers:manage",
                        "offers:create",
                        "offers:update",
                      ])(
                        <ActionButton
                          action="attach"
                          tooltip="Add Supporting Documents"
                          onClick={dispatchAction(value?.offer_id, "import")}
                        />
                      )}
                    </Show>
                    {withPermissions([
                      "*:*",
                      "offers:*",
                      "offers:manage",
                      "offers:delete",
                    ])(
                      <ActionButton
                        action="remove"
                        tooltip="Delete Offer"
                        onClick={dispatchAction(
                          value?.offer_id || "",
                          "remove"
                        )}
                      />
                    )}
                  </>
                ),
              },
            ]}
            data={offers}
            refetch={refetch}
            renderFilter={({ filterOpen, setFilterOpen }) => (
              <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
            )}
            // renderExport={({ exportOpen, setExportOpen }) => (
            //   <FilterMenu
            //     open={exportOpen}
            //     setOpen={setExportOpen}
            //   />
            // )}
          />
        </div>
      </div>
      <CreateOfferContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />

      {searchParams?.id && modal === "resolve" && (
        <ViewOfferSlipContainer
          open={modal === "resolve"}
          setOpen={(val: boolean) => setModal(val ? "resolve" : undefined)}
        />
      )}
      {searchParams?.id && modal === "remove" && (
        <RemoveContainer
          open={modal === "remove"}
          setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
        />
      )}

      {searchParams?.id && modal === "update" && (
        <UpdateOfferContainer
          open={modal === "update"}
          setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
          refetch={refetch}
        />
      )}

      {searchParams?.id && modal === "chat" && (
        <SendMessageContainer
          open={modal === "chat"}
          setOpen={(val: boolean) => setModal(val ? "chat" : undefined)}
        />
      )}
      {searchParams?.id && modal === "import" && (
        <AttachSupportingDocumentsContainer
          open={modal === "import"}
          setOpen={(val: boolean) => setModal(val ? "import" : undefined)}
        />
      )}
      {searchParams?.id && modal === "comment" && (
        <CommentContainer
          open={modal === "comment"}
          setOpen={(val: boolean) => setModal(val ? "comment" : undefined)}
        />
      )}
    </main>
  );
};

export default CreateSlipKEKPage;
